/**
 * Text-quote anchoring shared by the text-anchor controller (which paints the
 * highlight) and the chapter quiz (which needs to know where a quote lives
 * before the student clicks, to label its "Review 2.1 …" link).
 *
 * The anchor model and why it fails soft are in docs/highlights.md.
 */

// Chrome that is rebuilt at runtime, or never reading prose. The quiz and the
// flip-card deck repeat the chapter's own words; the outline repeats its
// headings; the quiz's "Back to Q1" button sits inside the prose it marked.
// A quote must resolve to the passage, not to an echo of it.
const SKIP = "script, style, .answers, .book-video, .chapter-quiz, .key-terms, .chapter-outline, .reading-progress, .text-anchor-status, .quiz-return, .section-number, .annotation-pin"

// Smooth unless the reader asked the OS for less motion.
export function scrollBehavior() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
}

export function normalizeQuote(value) {
  return (value || "").replace(/\s+/g, " ").trim()
}

/**
 * Flatten `root`'s visible text into one string, remembering where every
 * character came from. `map[i]` is the [textNode, offset] that produced
 * `text[i]`.
 *
 * Whitespace is collapsed as it is appended so that a stored quote (normalized
 * text) matches across line breaks and indentation in the source HTML.
 *
 * Hidden text is skipped. The authored quiz hides its source lists rather than
 * deleting them (the no-JS fallback), and a question often restates the very
 * sentence it asks about; a highlight there would scroll to nothing.
 */
export function indexText(root) {
  const seen = new Map()
  const visible = (el) => {
    if (!seen.has(el)) seen.set(el, typeof el.checkVisibility === "function" ? el.checkVisibility() : true)
    return seen.get(el)
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const parent = node.parentElement
      if (!parent || parent.closest(SKIP) || !visible(parent)) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    }
  })

  let text = ""
  let space = true // skips leading whitespace
  const map = []

  while (walker.nextNode()) {
    const value = walker.currentNode.nodeValue
    if (!value) continue

    for (let i = 0; i < value.length; i++) {
      if (/\s/.test(value[i])) {
        if (space) continue
        space = true
        text += " "
      } else {
        space = false
        text += value[i]
      }
      map.push([walker.currentNode, i])
    }
  }

  return { text, map }
}

// Where `quote` sits in `root`: the index plus the covered span, or null.
// Exact match first; a case-only difference is tolerated, nothing looser.
function find(root, quote) {
  const needle = normalizeQuote(quote)
  if (!root || !needle) return null

  const { text, map } = indexText(root)
  let at = text.indexOf(needle)
  if (at === -1) at = text.toLowerCase().indexOf(needle.toLowerCase())
  if (at === -1) return null

  return { map, at, length: needle.length }
}

// The first text node the quote covers, or null. Nothing is changed.
export function locateQuote(root, quote) {
  const found = find(root, quote)
  return found ? found.map[found.at][0] : null
}

/**
 * Find `quote` inside `root` and wrap it in <mark class="text-anchor-mark">s.
 * Returns the marks, or null when the quote isn't there.
 *
 * The wrapping is per text node on purpose. A quote that visually spans a
 * <span data-type="term"> or an <em> covers several text nodes, and there is
 * no single element to mark — so each covered run gets its own <mark> and CSS
 * makes the row read as one continuous highlight.
 */
export function paintQuote(root, quote) {
  const found = find(root, quote)
  if (!found) return null

  return wrapSpan(found, () => {
    const mark = document.createElement("mark")
    mark.className = "text-anchor-mark"
    return mark
  })
}

/**
 * Wrap the characters `found` covers (a `{ map, at, length }` span of an
 * indexText result) in elements made by `makeMark`. Returns the marks, or
 * null when nothing could be wrapped.
 *
 * The wrapping is per text node on purpose. A quote that visually spans a
 * <span data-type="term"> or an <em> covers several text nodes, and there is
 * no single element to mark — so each covered run gets its own <mark> and CSS
 * makes the row read as one continuous highlight.
 */
export function wrapSpan({ map, at, length }, makeMark) {
  // Group the covered characters by the text node they came from, so each
  // node is wrapped exactly once (wrapping splits it and would otherwise
  // invalidate the offsets of later characters in the same node).
  const runs = []
  for (let i = at; i < at + length; i++) {
    const entry = map[i]
    if (!entry) break

    const [node, offset] = entry
    const last = runs[runs.length - 1]
    if (last && last.node === node) last.end = offset
    else runs.push({ node, start: offset, end: offset })
  }

  // Wrap from the last run back: a later run in the same parent is then
  // untouched by an earlier wrap.
  const marks = runs
    .reverse()
    .map((run) => {
      try {
        const range = document.createRange()
        range.setStart(run.node, run.start)
        range.setEnd(run.node, run.end + 1)
        const mark = makeMark()
        range.surroundContents(mark)
        return mark
      } catch (_) {
        // A range that won't wrap (detached node, mid-edit DOM) loses its
        // mark; the rest of the highlight still paints.
        return null
      }
    })
    .filter(Boolean)
    .reverse()

  return marks.length ? marks : null
}

// ---------------------------------------------------------------------------
// Stored anchors (student highlights). The W3C Web Annotation selector set,
// as docs/highlights.md describes:
//
//   { quote: { exact, prefix, suffix }, position: { start, end }, element_id }
//
// Offsets are into indexText's normalized visible text of the scope, which
// is the same text model the quiz evidence links use. apache-annotator's
// matchers index raw DOM text (hidden nodes, uncollapsed whitespace), so we
// keep this one model instead of reconciling two.

const CONTEXT = 32

/**
 * Describe a live selection `range` inside `root` as an anchor, or null when
 * it covers no indexable text (all whitespace, or only runtime chrome such as
 * the quiz or the flip cards).
 */
export function describeRange(root, range) {
  const { text, map } = indexText(root)

  // A character is selected when both its edges are inside the range.
  let start = -1
  let end = -1
  const touches = new Map() // text node -> does the range reach it (skip the rest cheaply)
  for (let i = 0; i < map.length; i++) {
    const [node, offset] = map[i]
    if (!touches.has(node)) touches.set(node, range.intersectsNode(node))
    if (!touches.get(node)) {
      if (start !== -1) break
      continue
    }
    let inside = false
    try {
      inside = range.comparePoint(node, offset) === 0 && range.comparePoint(node, offset + 1) === 0
    } catch (_) {
      inside = false
    }
    if (inside) {
      if (start === -1) start = i
      end = i + 1
    } else if (start !== -1) {
      // Selections are contiguous; once we've left it we're done.
      if (range.comparePoint(node, offset) === 1) break
    }
  }
  if (start === -1) return null

  while (start < end && text[start] === " ") start++
  while (end > start && text[end - 1] === " ") end--
  if (end - start < 2) return null

  const exact = text.slice(start, end)
  const host = map[start][0].parentElement?.closest("[id]")
  const elementId = host && root.contains(host) && host !== root ? host.id : null

  return {
    quote: {
      exact,
      prefix: text.slice(Math.max(0, start - CONTEXT), start),
      suffix: text.slice(end, end + CONTEXT)
    },
    position: { start, end },
    ...(elementId ? { element_id: elementId } : {})
  }
}

/**
 * Resolve a stored anchor inside `root` to a `{ map, at, length }` span for
 * wrapSpan, or null. Tries the selectors in order and never guesses:
 *
 *   1. element id — search only that block (if it still exists)
 *   2. text quote — the exact words; when they occur more than once, the
 *      occurrence whose prefix/suffix agree best wins, then the one nearest the
 *      stored position
 *
 * No case folding and no fuzzy match: a highlight on the wrong words is worse
 * than none.
 */
export function resolveAnchor(root, anchor) {
  const exact = normalizeQuote(anchor?.quote?.exact)
  if (!root || !exact) return null

  const id = anchor.element_id
  if (id) {
    let host = null
    try { host = root.querySelector(`#${CSS.escape(id)}`) } catch (_) { host = null }
    if (host) {
      const found = bestMatch(indexText(host), exact, anchor, false)
      if (found) return found
    }
  }

  return bestMatch(indexText(root), exact, anchor, true)
}

function bestMatch({ text, map }, exact, anchor, usePosition) {
  const hits = []
  for (let at = text.indexOf(exact); at !== -1; at = text.indexOf(exact, at + 1)) hits.push(at)
  if (!hits.length) return null

  let best = hits[0]
  if (hits.length > 1) {
    const prefix = anchor.quote.prefix || ""
    const suffix = anchor.quote.suffix || ""
    const target = anchor.position?.start
    const score = (at) => {
      let s = 0
      while (s < prefix.length && text[at - 1 - s] === prefix[prefix.length - 1 - s]) s++
      let t = 0
      while (t < suffix.length && text[at + exact.length + t] === suffix[t]) t++
      return s + t
    }
    best = hits.reduce((a, b) => {
      const diff = score(b) - score(a)
      if (diff !== 0) return diff > 0 ? b : a
      if (!usePosition || typeof target !== "number") return a
      return Math.abs(b - target) < Math.abs(a - target) ? b : a
    })
  }

  return { map, at: best, length: exact.length }
}

// Undo every highlight (and its screen-reader note) under `root`, re-merging
// the text nodes the marks split so later quotes index cleanly.
export function clearMarks(root) {
  root.querySelectorAll(".text-anchor-status").forEach((note) => note.remove())
  root.querySelectorAll("mark.text-anchor-mark").forEach((mark) => {
    const parent = mark.parentNode
    if (!parent) return
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark)
    mark.remove()
    parent.normalize()
  })
}

// The nearest h2/h3 before `node` in reading order, skipping headings the
// runtime chrome builds (quiz, outline, hero).
export function sectionHeading(root, node) {
  if (!root || !node) return null

  let match = null
  root.querySelectorAll("h2, h3").forEach((heading) => {
    if (heading.closest(".chapter-quiz, .chapter-outline, .module-hero")) return
    if (heading.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING) match = heading
  })
  return match
}

// "2.1 Price, Cost, and Value" — the runtime number the outline controller
// stamped (if any) plus the heading's own words.
export function sectionLabel(heading) {
  if (!heading) return ""

  const number = normalizeQuote(heading.querySelector(".section-number")?.textContent)
  const clone = heading.cloneNode(true)
  clone.querySelectorAll(".section-number").forEach((span) => span.remove())
  const title = normalizeQuote(clone.textContent.replace(/ /g, " "))
  return [number, title].filter(Boolean).join(" ")
}
